/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  type ProductShelfACESResultsModel,
  type SkuRecordModel,
  type BreadcrumbV2Model,
  type GetProductShelfACESResultsParamsModel,
  type ProductShelfACESResponseModel,
} from '@/api/types/browse-search-types';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type ProductShelfACESResponse } from '../interface';
import { type AxiosInstance, isAxiosError } from 'axios';
import {
  type InfiniteData,
  type QueryClient,
  type QueryStatus,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { getAxios } from '@/lib/axios';
import { parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { pageTypes } from '@/constants/page';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { useRouter } from 'next/router';
import { useAppState } from '@/stores/AppState';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { usePageType } from './getPageType';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useCMSShelfPageConfig } from '@/features/contentstack/hooks/useCMSShelfPageConfig';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import logger from '@/utils/logger';
import { useMemo } from 'react';
import { getKiboDecisionFlag, useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { createQuery } from '@/utils/createReactQuery';
import type { Store } from '@/types';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';

type Options = {
  locale?: Locale;
  partGroupId: string;
  taxonomyPath?: string;
  tag?: string;
  canonicalPath?: string;
  pageType?: string;
  botEnabledFacetPath?: string;
  path?: string | undefined;
  recsPerPage?: string | undefined;
  makeModelYearPath?: string | undefined;
  storeId?: string | undefined;
  preview: boolean;
  ignoreVehicleSpecificProductsCheck?: boolean;
  rewardsId: string;
  eCookieId: string;
  pageNumber?: number;
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  vehicleQuestions?: string;
};

const shouldAddIgnoreVehicleParam = (
  pageType: string | undefined,
  makeId: number | undefined,
  modelId: number | undefined,
  vehicleTypeId: number | undefined,
  currentPath: string
): boolean => {
  return (
    pageType === 'ProductShelf' &&
    (!!makeId || !!modelId || !!vehicleTypeId) &&
    !currentPath.includes('ignoreVehicleSpecificProductsCheck')
  );
};

const getProductShelfACESResults = async (options: Options, axiosInstance?: AxiosInstance) => {
  const {
    eCookieId,
    locale,
    canonicalPath,
    partGroupId,
    pageType,
    preview,
    path,
    recsPerPage,
    botEnabledFacetPath,
    makeModelYearPath,
    rewardsId,
    tag,
    storeId,
    pageNumber,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
    ignoreVehicleSpecificProductsCheck,
  } = options;

  if (!canonicalPath) {
    throw new Error('canonicalPath must be provided in getCategoryShelfResults');
  }

  if (pageType !== pageTypes.ProductShelf && !tag) {
    throw new Error('No product shelf data on categories pages');
  }

  const country = getCountryFromLocale(locale || 'en-US');
  const parsedUrl = parseUrl(path ?? '');

  let sortValue = parsedUrl.query.sort;
  if (sortValue === 'best-seller-asc' || sortValue === 'best-seller-desc') {
    sortValue = 'best-sellers';
  } else if (sortValue === 'recently-added-desc') {
    sortValue = 'recently-added';
  } else if (sortValue === 'customer-rating-desc') {
    sortValue = 'customer-rating';
  }

  const params: GetProductShelfACESResultsParamsModel = {
    country,
    partGroupId,
    preview,
    pageNumber,
    recordsPerPage: recsPerPage
      ? Number(recsPerPage)
      : parsedUrl.query.recsPerPage
      ? Number(parsedUrl.query.recsPerPage)
      : undefined,
    sort: sortValue,
    storeId,
    ruleTags: tag,
    facet: parsedUrl.query.facet ?? undefined,
    botEnabledFacetPath,
    searchedKeyword: !parsedUrl.query.redirectUrl
      ? parsedUrl.query.filterByKeyWord ?? parsedUrl.query.searchText
      : undefined,
    makeModelYearPath: makeModelYearPath === '' ? undefined : makeModelYearPath,
    partNumberSearch: Boolean(parsedUrl.query.partNumberSearch) ?? undefined,
    ...(ignoreVehicleSpecificProductsCheck !== undefined && {
      ignoreVehicleSpecificProductsCheck,
    }),
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
  };

  const response = await getAxios(axiosInstance).get<ProductShelfACESResponseModel>(
    `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-browse-search-data/v1/product-shelves`,
    {
      withCredentials: false,
      ...(country === 'USA' && {
        headers: {
          eCookieId,
          ...(!!rewardsId && { rewardsId }),
        },
      }),
      params,
    }
  );
  return productShelfResultsSelector(response.data);
};

const productShelfResultsSelector = ({
  productShelfResults,
  xFusionQueryId,
}: ProductShelfACESResponseModel): ProductShelfACESResponse => {
  if (!productShelfResults) {
    return {
      productShelfResults,
      xFusionQueryId,
    };
  }

  // Map the breadcrumbs to maintain backward compatibility with old field names
  const mappedBreadCrumbs = productShelfResults.breadCrumbs?.map((breadcrumb) => {
    return {
      taxonomyId: breadcrumb.categoryIdPath,
      displayName: breadcrumb.categoryName,
      taxonomyPath: breadcrumb.taxonomyPath,
      pageType: breadcrumb.pageType,
      canonicalPath: breadcrumb.canonicalUrl,
    };
  });
  return {
    productShelfResults: {
      ...productShelfResults,
      breadCrumbs: mappedBreadCrumbs as BreadcrumbV2Model[],
    },
    xFusionQueryId,
  };
};

export const {
  useData: useACESProductShelfResultsData,
  prefetch: prefetchACESProductShelfResultsData,
  query: productsShelfACESQuery,
} = createQuery<ProductShelfACESResponse, Options>(
  'productshelf-results',
  async (options, axiosInstance) => getProductShelfACESResults(options, axiosInstance)
);

export const useProductShelfACESResults = ({
  enabled = true,
  ...rest
}: {
  enabled?: boolean;
} = {}) => {
  const router = useRouter();
  const headerResult = useHeaderData();
  const preferredVehicle = getPreferredVehicle(headerResult.data);
  const locale = useLocale();
  const appState = useAppState();
  const { recordsPerPage } = useCMSShelfPageConfig();
  const isMerchShelfPage = router.route === '/deals/[cmsPath]/[[...slug]]';
  const { data: plpCMSData, isSuccess: isPLPCMSDataSuccess } = useContentStackPLPData({
    enabled: true,
  });
  const isLoadMoreEnabled = useMonetateDecisionFlag('loadMoreEnabled');

  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = __IS_SERVER__ ? appState.state.reqUrl : router.asPath;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }

  const { data: pageTypeData } = usePageType();
  const parsedUrl = parseUrl(reqUrl);

  const lwTag = !!plpCMSData?.additional_attributes_merch_shelf_page?.lucidworks_fusion_tag
    ? plpCMSData.additional_attributes_merch_shelf_page.lucidworks_fusion_tag
    : undefined;

  const taxonomyPath = pageTypeData?.taxonomyPath || undefined;
  delete parsedUrl.query.loadMore;
  const cleanedReqUrl = stringifyUrl(parsedUrl);
  const { data: headerData } = useHeaderData();
  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();
  const vehicleQuestions = useMemo(
    () =>
      extractVehicleQuestions(
        headerData?.vehicleMap.acesVehicleAttributes?.vehicleQuestionsIds,
        true
      ) as string,
    [headerData?.vehicleMap]
  );
  const constructedPath = useMemo(() => {
    if (
      shouldAddIgnoreVehicleParam(
        pageTypeData?.pageType,
        preferredVehicle?.makeId,
        preferredVehicle?.modelId,
        preferredVehicle?.vehicleTypeId,
        cleanedReqUrl || ''
      )
    ) {
      const separator = (cleanedReqUrl || '').includes('?') ? '&' : '?';
      return `${cleanedReqUrl}${separator}ignoreVehicleSpecificProductsCheck=false`;
    }
    return cleanedReqUrl || '';
  }, [
    pageTypeData?.pageType,
    preferredVehicle?.makeId,
    preferredVehicle?.modelId,
    preferredVehicle?.vehicleTypeId,
    cleanedReqUrl,
  ]);

  const constructedParsedUrl = parseUrl(constructedPath);
  const ignoreVehicleSpecificProductsCheckValue =
    constructedParsedUrl.query.ignoreVehicleSpecificProductsCheck;

  const options: Options = {
    rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
    eCookieId: appState.state.eCookieId,
    locale,
    partGroupId: pageTypeData?.catalogRepositoryId || '',
    pageType: pageTypeData?.pageType || undefined,
    canonicalPath: pageTypeData?.canonicalPath || parsedUrl.url,
    taxonomyPath,
    tag: lwTag,
    botEnabledFacetPath: pageTypeData?.botEnabledFacetPath || undefined,
    makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
    recsPerPage: recordsPerPage ? String(recordsPerPage) : undefined,
    storeId: headerResult.data?.storeNumber ?? undefined,
    pageNumber:
      typeof router.query.currentPage === 'string'
        ? Number(router.query.currentPage)
        : typeof router.query.pageNumber === 'string'
        ? Number(router.query.pageNumber)
        : 1,
    path: router.route === '/cms/taxonomy-selector' ? taxonomyPath : constructedPath,
    preview: showXMPreviewDate(),
    ...rest,
    ...(preferredVehicle?.year && { year: Number(preferredVehicle?.year) }),
    makeId: preferredVehicle?.makeId,
    ...(ignoreVehicleSpecificProductsCheckValue !== undefined && {
      ignoreVehicleSpecificProductsCheck:
        ignoreVehicleSpecificProductsCheckValue === 'true'
          ? true
          : ignoreVehicleSpecificProductsCheckValue === 'false'
          ? false
          : undefined,
    }),
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    ...(!!vehicleQuestions && { vehicleQuestions }),
  };
  let infiniteQueryData: InfiniteData<ProductShelfACESResponse> | undefined;
  let fetchNextPageProductShelfResults = () => Promise.resolve();
  let hasNextPageProductShelfResults: boolean | undefined = false;
  let fetchPreviousPageProductShelfResults = () => Promise.resolve();
  let hasPreviousPageProductShelfResults: boolean | undefined = false;
  let isFetchingProductShelfResults: boolean | undefined = false;
  let isPendingProductShelfResults: boolean | undefined = false;
  let statusProductShelfResults: QueryStatus | 'idle' = 'idle';
  let refetchProductShelfResults = () => Promise.resolve();
  const infiniteQueryResult = useInfiniteQuery({
    queryKey: [...productsShelfACESQuery.getFullKey(options), 'infinite'],
    queryFn: ({ pageParam }) => {
      return getProductShelfACESResults({
        ...options,
        pageNumber: pageParam,
        recsPerPage: '24',
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalRecords = lastPage?.productShelfResults?.totalNumberOfRecords ?? 0;
      const recsPerPage = Number(options.recsPerPage) || 24;
      const currentPageNumber = allPages.length;
      const totalPages = Math.ceil(totalRecords / recsPerPage);

      if (currentPageNumber < totalPages) {
        return currentPageNumber + 1;
      }
      return undefined;
    },
    enabled: isMerchShelfPage
      ? isPLPCMSDataSuccess &&
        !!lwTag &&
        acesEnabled &&
        acesNonVehicleEndpointsEnabled &&
        isLoadMoreEnabled
      : enabled &&
        isLoadMoreEnabled &&
        pageTypeData?.pageType === 'ProductShelf' &&
        (router.route === '/[...seoUrlPath]' || router.route === '/cms/taxonomy-selector') &&
        acesEnabled &&
        acesNonVehicleEndpointsEnabled,

    staleTime: 30 * 60000,
    retry: 1,
    retryOnMount: false,
  });

  useQueryResultOnError(infiniteQueryResult, async (err) => {
    logger.error({
      message: 'Error fetching product shelf results',
      meta: {
        error: isAxiosError<unknown>(err) ? err.response?.data : err,
      },
    });

    try {
      await router.replace('/errorPage');
    } catch {
      throw new Error(`There was a problem redirecting to /errorPage`);
    }
  });

  const regularQueryResult = useACESProductShelfResultsData({
    ...options,
    enabled: isMerchShelfPage
      ? isPLPCMSDataSuccess &&
        !!lwTag &&
        acesEnabled &&
        acesNonVehicleEndpointsEnabled &&
        !isLoadMoreEnabled
      : enabled &&
        !isLoadMoreEnabled &&
        pageTypeData?.pageType === 'ProductShelf' &&
        (router.route === '/[...seoUrlPath]' || router.route === '/cms/taxonomy-selector') &&
        acesEnabled &&
        acesNonVehicleEndpointsEnabled,
    staleTime: 30 * 60000,
    retry: 1,
    pageNumber: constructedParsedUrl.query.pageNumber
      ? Number(constructedParsedUrl.query.pageNumber)
      : 1,
  });

  useQueryResultOnError(regularQueryResult, async () => {
    try {
      await router.replace('/errorPage');
      return;
    } catch {
      throw new Error(`There was a problem redirecting to /errorPage`);
    }
  });

  if (isLoadMoreEnabled) {
    const {
      data,
      fetchNextPage,
      hasNextPage,
      fetchPreviousPage,
      hasPreviousPage,
      isFetching,
      isPending,
      status,
      refetch,
    } = infiniteQueryResult;
    fetchNextPageProductShelfResults = () => fetchNextPage().then(() => {});
    hasNextPageProductShelfResults = hasNextPage;
    fetchPreviousPageProductShelfResults = () => fetchPreviousPage().then(() => {});
    hasPreviousPageProductShelfResults = hasPreviousPage;
    isPendingProductShelfResults = isPending;
    statusProductShelfResults = status;
    isFetchingProductShelfResults = isFetching;
    refetchProductShelfResults = () => refetch().then(() => {});
    infiniteQueryData = data;
  }

  const mergedData = useMemo(() => {
    if (!infiniteQueryData?.pages) return undefined;

    const allSkuRecords: SkuRecordModel[] = [];
    let lastProductShelfResults: ProductShelfACESResultsModel | undefined;
    let lastXFusionQueryId: string | undefined;

    infiniteQueryData.pages.forEach((page) => {
      if (page?.productShelfResults?.skuRecords) {
        allSkuRecords.push(...page.productShelfResults.skuRecords);
        lastProductShelfResults = page.productShelfResults;
        lastXFusionQueryId = page.xFusionQueryId;
      }
    });

    if (!lastProductShelfResults) return undefined;

    return {
      productShelfResults: {
        ...lastProductShelfResults,
        skuRecords: allSkuRecords,
      },
      xFusionQueryId: lastXFusionQueryId,
    };
  }, [infiniteQueryData]);

  return {
    data: isLoadMoreEnabled ? mergedData : regularQueryResult.data,
    pageNumber: isLoadMoreEnabled ? infiniteQueryData?.pages.length ?? 1 : options.pageNumber,
    fetchNextPage: fetchNextPageProductShelfResults,
    hasNextPage: hasNextPageProductShelfResults,
    fetchPreviousPage: fetchPreviousPageProductShelfResults,
    hasPreviousPage: hasPreviousPageProductShelfResults,
    isFetching: isLoadMoreEnabled ? isFetchingProductShelfResults : regularQueryResult.isFetching,
    isPending: isLoadMoreEnabled ? isPendingProductShelfResults : regularQueryResult.isPending,
    status: isLoadMoreEnabled ? statusProductShelfResults : regularQueryResult.status,
    refetch: isLoadMoreEnabled ? refetchProductShelfResults : regularQueryResult.refetch,
  };
};

export const prefetchProductShelfACESResults = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Options
) => {
  return prefetchACESProductShelfResultsData(queryClient, options, axiosInstance);
};

export const prefetchProductShelfACESResultsInfinite = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Options
) => {
  return queryClient.prefetchInfiniteQuery({
    queryKey: [...productsShelfACESQuery.getFullKey(options), 'infinite'],
    queryFn: ({ pageParam }) =>
      getProductShelfACESResults(
        {
          ...options,
          pageNumber: pageParam,
          recsPerPage: options.recsPerPage as string,
        },
        axiosInstance
      ),
    initialPageParam: 1,
  });
};

export const getProductShelfACESResultsFromCache = (
  queryClient: QueryClient,
  store: Store,
  options: Options
): ProductShelfACESResponse | undefined => {
  const isLoadMoreEnabled = getKiboDecisionFlag(queryClient, 'loadMoreEnabled');
  const { appData } = store.getState();
  if (isLoadMoreEnabled) {
    return queryClient.getQueryData<InfiniteData<ProductShelfACESResponse>>([
      ...productsShelfACESQuery.getFullKey({
        recsPerPage: appData?.deviceType === 'bot' ? '24' : undefined,
        ...options,
      }),
      'infinite',
    ])?.pages[0];
  }
  return queryClient.getQueryData<ProductShelfACESResponse>(
    productsShelfACESQuery.getFullKey({
      recsPerPage: appData?.deviceType === 'bot' ? '24' : undefined,
      ...options,
    })
  );
};

export const useProductShelfACESFacets = () => {
  const router = useRouter();
  const headerResult = useHeaderData();
  const locale = useLocale();
  const appState = useAppState();

  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = __IS_SERVER__ ? appState.state.reqUrl : router.asPath;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }

  const { data: pageTypeData } = usePageType();

  const commonOptions = {
    eCookieId: appState.state.eCookieId,
    rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
    locale,
    vehicleId: undefined,
    pageType: pageTypeData?.pageType ?? 'ProductShelf',
    canonicalPath: pageTypeData?.canonicalPath ?? parseUrl(reqUrl).url,
    taxonomyPath: pageTypeData?.taxonomyPath,
    botEnabledFacetPath: undefined,
    makeModelYearPath: undefined,
    recsPerPage: '1',
    storeId: headerResult.data?.storeNumber ?? undefined,
    path: reqUrl,
    preview: showXMPreviewDate(),
    staleTime: 30 * 60000,
    retry: 1,
    retryOnMount: false,
  };

  return useACESProductShelfResultsData({
    ...commonOptions,
    partGroupId: pageTypeData?.catalogRepositoryId || '',
  });
};
